import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Box, FormControlLabel, Switch } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  FieldTypesEnum,
  getHalcyonSpecificYearOptions,
  halcyonFormGroupBy,
  HalcyonMultiYearEnum,
  halcyonYearModeOptions,
} from 'constants/loanActionEditor';

import { selectHalcyonFormPickerOptions } from 'store/loanActions/halcyonFormCatalog';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';
import { wrapperClassname } from '../LoanActionItem.constants';
import { DynamicValueInput } from '../LoanActionItems';

const halcyonSpecificYearOptions = getHalcyonSpecificYearOptions();

const LoanActionHalcyonAddFormsRow = ({
  index,
  rootPath,
  isDisabled,
  isEditAllowed,
  isAutomatedActions,
}) => {
  const { form, setInForm } = useFormContext();

  const halcyonFormOptions = useSelector(selectHalcyonFormPickerOptions);

  const fieldPath = `${rootPath}[${index}]`;
  const formsFieldPath = `${fieldPath}.forms`;
  const yearModeFieldPath = `${fieldPath}.yearMode`;
  const specificYearsFieldPath = `${fieldPath}.specificYears`;
  const allowManualSelectionFieldPath = `${fieldPath}.allowManualSelection`;

  const yearMode = _.get(form, yearModeFieldPath);
  const allowManualSelection = _.get(form, allowManualSelectionFieldPath);
  const isSpecificYears = yearMode?.value === HalcyonMultiYearEnum.SPECIFIC;
  const areFieldsDisabled = isDisabled || !isEditAllowed;
  const areFormFieldsVisible = !allowManualSelection || isAutomatedActions;

  const onToggleAllowManualSelection = useCallback(
    e => setInForm(allowManualSelectionFieldPath, e.target.checked),
    [setInForm, allowManualSelectionFieldPath],
  );

  return (
    <Box className={wrapperClassname}>
      {!isAutomatedActions && (
        <FormControlLabel
          control={
            <Switch
              checked={!!allowManualSelection}
              disabled={areFieldsDisabled}
              onChange={onToggleAllowManualSelection}
            />
          }
          data-testid={loanManagementTestIds.getHalcyonManualSelectionSwitchId(fieldPath)}
          label="Allow manual selection at execution time"
        />
      )}
      {areFormFieldsVisible && (
        <>
          <DynamicValueInput
            required
            disabled={areFieldsDisabled}
            groupBy={halcyonFormGroupBy}
            label="Forms"
            name={formsFieldPath}
            options={halcyonFormOptions}
            type={FieldTypesEnum.MULTISELECT}
          />
          <DynamicValueInput
            required
            disabled={areFieldsDisabled}
            label="Year Mode"
            name={yearModeFieldPath}
            options={halcyonYearModeOptions}
            type={FieldTypesEnum.SELECT}
          />
          {isSpecificYears && (
            <DynamicValueInput
              required
              disabled={areFieldsDisabled}
              label="Specific Years"
              name={specificYearsFieldPath}
              options={halcyonSpecificYearOptions}
              type={FieldTypesEnum.MULTISELECT}
            />
          )}
        </>
      )}
    </Box>
  );
};

LoanActionHalcyonAddFormsRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionHalcyonAddFormsRow);
